import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import DynamicBundleSummary from './DynamicBundleSummary';
import ProductAccessoriesTabs from './ProductAccessoriesTabs';
import FeedbackMessages from './FeedbackMessages';

import {
  dispatchSetProductBundlesListForDynamicBundle,
  dispatchAddProductToBundle,
  dispatchRemoveProductFromBundle
} from '../productPageBundlesAnalytics';

import UILocale from '../../../../utils/UILocale';


const DynamicBundles = ({
  mainProductId, updateDynamicBundle, accessoriesCategories,
  dynamicBundleMax, dynamicBundleLabel, dynamicBundlePrice, dynamicBundleImageUrl, isMobile
}) => {

  const headerLabelRef = useRef(null);
  const [dynamicBundleState, setDynamicBundleState] = useState({
    bundleCount: 0,
    bundleFull: false,
    bundleName: dynamicBundleLabel,
    bundlePrice: dynamicBundlePrice,
    products: [],
    disabledSegments: [],
    bundleID: null
  });

  const [showBundleFullErrorMessage, setShowBundleFullErrorMessage] = useState(false);
  const [showBundleUpdateErrorMessage, setShowBundleUpdateErrorMessage] = useState(false);
  const [updatingBundle, setUpdatingBundle] = useState(false);
  const [showBundleUpdateSuccessfulMessage, setShowBundleUpdateSuccessfulMessage] =
    useState(false);

  // first fetch immediately after render to ask for banners data
  // called only once after rendering of the component
  useEffect(() => {
    let isCancelled = false;

    const restoreDynamicBundle = async () => {
      const dynamicBundleResponse = await updateDynamicBundle('restore');
      if (!isCancelled) {
        if (dynamicBundleResponse.ok) {
          // analytics
          if (dynamicBundleResponse.data.products &&
            dynamicBundleResponse.data.products.length > 1) {
            dispatchSetProductBundlesListForDynamicBundle([{
              bundleType: 'userCreated'
            }]);
          }

          setDynamicBundleState({
            ...dynamicBundleResponse.data,
            bundleName:
              dynamicBundleResponse.data.bundleName
                ? dynamicBundleResponse.data.bundleName : dynamicBundleLabel,
            bundlePrice:
              dynamicBundleResponse.data.bundlePrice
                ? dynamicBundleResponse.data.bundlePrice : dynamicBundlePrice
          });
        } else {
          console.error(dynamicBundleResponse.error.message);
        }
      }
    };

    restoreDynamicBundle();

    // cleaning up request according to this tutorial
    // https://dev.to/pallymore/clean-up-async-requests-in-useeffect-hooks-90h
    return () => {
      isCancelled = true;
    };

  }, []);

  return (
    <div className={'packBuilder'} id={'packBuilder'}>
      <h4 ref={headerLabelRef}>{UILocale.getString('dynamic.bundles.build_your_own_pack')}</h4>

      <p>
        {UILocale.getString('dynamic.bundles.bundle_builder_text1')}
        <br />
        <small><em>{UILocale.getString('dynamic.bundles.bundle_builder_text2')}</em></small>
      </p>

      <h5 className={'nosp'}>
        {UILocale.getString('dynamic.bundles.bundle_builder_up_to')}
        {' '}
        {dynamicBundleMax}
        {' '}
        {UILocale.getString('dynamic.bundles.bundle_builder_items')}
      </h5>

      <div className={'border plug'}>
        <DynamicBundleSummary
          mainProductId={mainProductId}
          dynamicBundleHeaderLabel={dynamicBundleState.bundleName}
          dynamicBundlePrice={dynamicBundleState.bundlePrice}
          dynamicBundleSavings={dynamicBundleState.bundleSavings}
          dynamicBundleSeparateSellingPrice={dynamicBundleState.bundleSeparateSellingPrice}
          dynamicBundleImageUrl={dynamicBundleImageUrl}
          dynamicBundleMax={dynamicBundleMax}
          productsAddedToDynamicBundle={dynamicBundleState.products.slice(1)}
          removeProductFromBundle={async (productID) => {
            setUpdatingBundle(true);
            const dynamicBundleResponse = await updateDynamicBundle('remove', productID);
            setUpdatingBundle(false);

            if (dynamicBundleResponse.ok) {
              setDynamicBundleState({
                ...dynamicBundleResponse.data,
                bundleName:
                  dynamicBundleResponse.data.bundleName
                    ? dynamicBundleResponse.data.bundleName : dynamicBundleLabel,
                bundlePrice:
                  dynamicBundleResponse.data.bundlePrice
                    ? dynamicBundleResponse.data.bundlePrice : dynamicBundlePrice
              });
              setShowBundleUpdateSuccessfulMessage(true);
              setTimeout(() => { setShowBundleUpdateSuccessfulMessage(false); }, 3500);

              // analytics
              dispatchRemoveProductFromBundle(productID);
            } else {
              console.error(dynamicBundleResponse.error.message);
              setShowBundleUpdateErrorMessage(true);
              setTimeout(() => { setShowBundleUpdateErrorMessage(false); }, 3500);
            }
          }}
          restartDynamicBundleCreation={async () => {
            setUpdatingBundle(true);
            const dynamicBundleResponse = await updateDynamicBundle('restart');
            setUpdatingBundle(false);
            if (dynamicBundleResponse.ok) {
              setDynamicBundleState({
                ...dynamicBundleResponse.data,
                bundleName: dynamicBundleLabel,
                bundlePrice: dynamicBundlePrice
              });
              setShowBundleUpdateSuccessfulMessage(true);
              setTimeout(() => { setShowBundleUpdateSuccessfulMessage(false); }, 3500);
            } else {
              console.error(dynamicBundleResponse.error.message);
              setShowBundleUpdateErrorMessage(true);
              setTimeout(() => { setShowBundleUpdateErrorMessage(false); }, 3500);
            }
          }}
          showRestartButton={dynamicBundleState.bundleCount > 0}
          showAddToBasketButton={dynamicBundleState.bundleCount > 0 &&
            typeof dynamicBundleState.bundleID === 'string'}
          bundleID={dynamicBundleState.bundleID}
          bundleProductIds={dynamicBundleState.products.map((product) => product.productID)}
        />

        <ProductAccessoriesTabs
          accessoriesCategories={accessoriesCategories}
          isMobile={isMobile}
          addProductToBundle={async (productID) => {
            if (!dynamicBundleState.bundleFull) {
              setUpdatingBundle(true);
              const dynamicBundleResponse = await updateDynamicBundle('add', productID);
              setUpdatingBundle(false);
              if (dynamicBundleResponse.ok) {
                setDynamicBundleState(dynamicBundleResponse.data);
                setShowBundleUpdateSuccessfulMessage(true);
                setTimeout(() => { setShowBundleUpdateSuccessfulMessage(false); }, 3500);

                // analytics
                dispatchAddProductToBundle(productID);
              } else {
                console.error(dynamicBundleResponse.error.message);
                setShowBundleUpdateErrorMessage(true);
                setTimeout(() => { setShowBundleUpdateErrorMessage(false); }, 3500);
              }
            } else {
              setShowBundleFullErrorMessage(true);
              setTimeout(() => { setShowBundleFullErrorMessage(false); }, 3500);
              headerLabelRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }}
          disabledSegments={dynamicBundleState.disabledSegments}
        />

      </div>

      <FeedbackMessages
        headerLabelRef={headerLabelRef}
        updatingBundle={updatingBundle}
        showBundleFullErrorMessage={showBundleFullErrorMessage}
        showBundleUpdateErrorMessage={showBundleUpdateErrorMessage}
        showBundleUpdateSuccessfulMessage={showBundleUpdateSuccessfulMessage}
      />
    </div>
  );
};

DynamicBundles.propTypes = {
  mainProductId: PropTypes.string.isRequired,
  dynamicBundleMax: PropTypes.number.isRequired,
  accessoriesCategories: PropTypes.arrayOf(
    PropTypes.shape({
      iSegmentId: PropTypes.number.isRequired,
      iLimit: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      accessories: PropTypes.arrayOf(PropTypes.shape({
        sProductId: PropTypes.string,
        sLabel: PropTypes.string,
        sFullLabel: PropTypes.string,
        sImageUrl: PropTypes.string,
        sImageBigUrl: PropTypes.string,
        sComplementaryLabel: PropTypes.string,
        sDescription: PropTypes.string,
        fSeparatePrice: PropTypes.number,
        bIsAvailable: PropTypes.bool,
        bContainsSHD: PropTypes.bool,
        bIsSHD: PropTypes.bool,
        canBeHomeDelivered: PropTypes.bool,
        isFV: PropTypes.bool,
        canBeDownloaded: PropTypes.bool,
        canBeReservedandCollected: PropTypes.bool,
        canBePayedAndCollected: PropTypes.bool,
        isForwardOrderable: PropTypes.bool,
        isPreOrderable: PropTypes.bool,
        bIsDiscount: PropTypes.bool,
        sPrice: PropTypes.string,
        sSeparatePrice: PropTypes.string,
        sDiscount: PropTypes.string
      })).isRequired
    })
  ).isRequired,
  updateDynamicBundle: PropTypes.func.isRequired,
  dynamicBundleLabel: PropTypes.string.isRequired,
  dynamicBundlePrice: PropTypes.string.isRequired,
  dynamicBundleImageUrl: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
  isMobile: state.general.device.deviceType === 'mobile'
});

export default connect(mapStateToProps)(DynamicBundles);
